import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PORTAL_PLAN_PICKER_PATH } from 'modules/portal-plans/paths';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { insightsErrorCode, unlockAttemptInsights } from './api';
import type { ExamInsightsState } from './context';

/** The refusals a plan fixes: the plans page is the way on. Any other failure is shown and can be retried. */
const NEEDS_PLAN = new Set(['PORTAL_PLAN_REQUIRED', 'ANALYSIS_LIMIT_REACHED']);

/**
 * The unlock flow of one IELTS/SAT result: a learner known to have no analyses left goes straight to the
 * plan picker; otherwise (credits left, or the plan not loaded yet) the server decides. `onUnlocked`
 * reloads the result.
 */
export function useExamInsightsUnlock(attemptId: string | undefined, locked: boolean, onUnlocked: () => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: subscription } = usePortalSubscription();
  const analysesRemaining = subscription?.isActive ? (subscription.analysesRemaining ?? 0) : 0;
  // Only a loaded plan can say there is nothing to spend; before that, ask the server.
  const knownEmpty = subscription !== undefined && analysesRemaining <= 0;
  const inFlight = useRef(false);
  const [failed, setFailed] = useState(false);

  const unlock = useMutation({
    mutationFn: () => unlockAttemptInsights(attemptId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-subscription'] });
      onUnlocked();
    },
    onError: (error) => {
      if (NEEDS_PLAN.has(insightsErrorCode(error) ?? '')) navigate(PORTAL_PLAN_PICKER_PATH);
      else setFailed(true);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const requestUnlock = useCallback(() => {
    if (!attemptId || inFlight.current) return;
    if (knownEmpty) {
      navigate(PORTAL_PLAN_PICKER_PATH);
      return;
    }
    inFlight.current = true;
    setFailed(false);
    unlock.mutate();
  }, [attemptId, knownEmpty, navigate, unlock]);

  const state = useMemo<ExamInsightsState>(
    () => ({ locked, requestUnlock, unlocking: unlock.isPending, failed }),
    [locked, requestUnlock, unlock.isPending, failed],
  );
  return { state, analysesRemaining, analysisLimit: subscription?.isActive ? (subscription.analysisLimit ?? 0) : 0 };
}
